import { FC } from "react";

import { getOppositeLocale } from "@simplyk/common";

import { getEnabledReminderTranslations } from "../helpers/reminderTranslations";
import { TranslationModalLayout } from "../TranslationModalLayout/TranslationModalLayout";

import { TicketingTranslationForm } from "./TicketingTranslationForm";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { EmailContentsLoadState } from "@/features/LiveFormEditor/common/EmailContentsLoadState";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";

interface TicketingTranslationModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onSave: () => void;
}

export const TicketingTranslationModal: FC<TicketingTranslationModalProps> = ({ isOpen, closeModal, onSave }) => {
  const { organization } = useCurrentUserContext();
  const { apiFormObject, remindersData: reminderVisibility } = useLiveFormEditorContext<"ticketing">();
  const formLocale = apiFormObject.locale;
  const translationLocale = getOppositeLocale({ organizationCountry: organization?.country, locale: formLocale });
  const hasReminders = reminderVisibility.showReminders;
  const remindersQuery = trpc.getLiveFormRemindersData.useQuery(
    { ticketingId: apiFormObject.id },
    { enabled: isOpen && hasReminders }
  );
  const emailContentsQuery = trpc.getAutomatedEmailContents.useQuery(
    { ticketingId: apiFormObject.id },
    { enabled: isOpen }
  );
  const isLoading = (hasReminders && remindersQuery.isPending) || emailContentsQuery.isPending;
  const hasError = (hasReminders && remindersQuery.isError) || emailContentsQuery.isError;

  if (isLoading || hasError || !emailContentsQuery.data) {
    return (
      <TranslationModalLayout
        open={isOpen}
        onClose={closeModal}
        onSubmit={(event) => event.preventDefault()}
        onSwap={() => {}}
        formLocale={formLocale}
        translationLocale={translationLocale}
        organizationCountry={organization?.country}
        isTranslating={isLoading}
        actionsDisabled
        slots={{
          content: (
            <EmailContentsLoadState
              hasError={hasError}
              onRetry={() => {
                if (hasReminders) {
                  void remindersQuery.refetch();
                }
                void emailContentsQuery.refetch();
              }}
            />
          ),
        }}
      />
    );
  }

  return (
    <TicketingTranslationForm
      closeModal={closeModal}
      onSave={onSave}
      reminders={getEnabledReminderTranslations(hasReminders ? remindersQuery.data : undefined, translationLocale)}
      emailContents={emailContentsQuery.data}
    />
  );
};
